// web/src/api/quality/realtime.ts —— 实时质检命中 / 情绪序列（管理台读端点）
// 拆自 web/src/api/quality.ts 的同名分区（内容原样搬运）；由 web/src/api/quality.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put } from '../../request';
import { toPage, type Page, type PageQuery } from '../types';
import type { TranscriptSegment } from '../cdr';

import type { AddViolationIn, QualityReport } from './reports';

// ---------- 实时质检命中 / 情绪序列（管理台读端点） ----------

export interface RealtimeHit {
  id: number;
  uuid?: string;
  cdr_id?: number;
  ref_chat_id?: number;
  agent_id?: number;
  rule_id?: number;
  name?: string;
  type?: string; // keyword|forbidden|silence|overlap
  dimension?: string;
  level?: string;
  deduct?: number;
  evidence?: string; // JSON
  emotion?: string;
  complaint_type?: string;
  escalation_prob?: number;
  status?: string; // pending|merged
  merged_at?: string | null;
  /** rule=规则命中 / emotion=情绪预警 / trend=情绪升级趋势 */
  alert_type?: string;
  /** 实际推送时刻（同规则 ≥5s 节流后；被节流为空） */
  pushed_at?: string | null;
  /** 触发告警的话语结束墙钟（时延口径：语音结束 → 推送） */
  utterance_end_at?: string | null;
  /** 坐席即时打标（L1）：none|tp|fp|handled */
  feedback_verdict?: string;
  feedback_by?: number | null;
  feedback_at?: string | null;
  feedback_note?: string;
  /** 质检员权威裁定（L2）：none|tp|fp —— 误报率唯一口径 */
  review_verdict?: string;
  reviewed_by?: number | null;
  reviewed_at?: string | null;
  /** 挂机合并生成的违规点 id（0=未合并） */
  violation_id?: number;
  created_at?: string;
  [k: string]: any;
  /** 低分告警（alert_type=rule_low_score）触发规则的实际得分（0–5）；其余告警为空 */
  awarded?: number | null;
}

/** GET /quality/realtime-hits?cdr_id=&ref_chat_id=&channel=&type=&status=&alert_type=&feedback_verdict=（admin/supervisor；channel=call|chat 空=全部） */
export async function listRealtimeHits(
  q: {
    cdr_id?: number;
    ref_chat_id?: number;
    /** call=通话 / chat=在线会话；空=全部渠道 */
    channel?: 'call' | 'chat';
    type?: string;
    status?: string;
    alert_type?: string;
    feedback_verdict?: string;
  } & PageQuery
): Promise<Page<RealtimeHit>> {
  return toPage<RealtimeHit>(await get('/quality/realtime-hits', { page: 1, pageSize: 50, ...(q || {}) }));
}

/** POST /quality/realtime-hits/:id/review —— 质检员裁定告警（tp=准确 / fp=误报） */
export async function reviewRealtimeHit(id: number, verdict: 'tp' | 'fp'): Promise<RealtimeHit> {
  const data: any = await post(`/quality/realtime-hits/${id}/review`, { verdict });
  return (data || {}) as RealtimeHit;
}

/** POST /quality/realtime-hits/:id/feedback —— 管理台代坐席打标（L1，可改）：tp=准确 / fp=误报 / handled=已处理；已裁定则拒绝 */
export async function feedbackRealtimeHit(id: number, verdict: 'tp' | 'fp' | 'handled'): Promise<RealtimeHit> {
  const data: any = await post(`/quality/realtime-hits/${id}/feedback`, { verdict });
  return (data || {}) as RealtimeHit;
}

/** 批量裁定/打标结果：updated=实际更新数；skipped=跳过数（已裁定或不存在） */
export interface RealtimeBatchResult {
  updated: number;
  skipped?: number;
  ids?: number[];
  skipped_ids?: number[];
}

/** POST /quality/realtime-hits/batch-review —— 批量质检员裁定（tp=准确 / fp=误报） */
export async function batchReviewRealtimeHits(ids: number[], verdict: 'tp' | 'fp'): Promise<RealtimeBatchResult> {
  const data: any = await post('/quality/realtime-hits/batch-review', { ids, verdict });
  return (data || {}) as RealtimeBatchResult;
}

/** POST /quality/realtime-hits/batch-feedback —— 批量坐席打标（L1，可改）：tp=准确 / fp=误报 / handled=已处理；已裁定的行跳过 */
export async function batchFeedbackRealtimeHits(ids: number[], verdict: 'tp' | 'fp' | 'handled'): Promise<RealtimeBatchResult> {
  const data: any = await post('/quality/realtime-hits/batch-feedback', { ids, verdict });
  return (data || {}) as RealtimeBatchResult;
}

export interface AlertPrecisionBucket {
  key: string;
  name?: string;
  reviewed: number;
  tp: number;
  fp: number;
  fp_rate: number;
  precision: number;
}

export interface AlertPrecisionStats {
  summary: {
    total: number;
    pushed: number;
    reviewed: number;
    tp: number;
    fp: number;
    precision: number;
    fp_rate: number;
    pending_l1: number;
    unresolved: number;
  };
  by_type: AlertPrecisionBucket[];
  by_rule: AlertPrecisionBucket[];
  by_day: AlertPrecisionBucket[];
  date_from?: string;
  date_to?: string;
  alert_type?: string;
}

/** GET /quality/stats/alert-precision?dateFrom=&dateTo=&alert_type=&channel=（误报率：分母=已裁定告警数；channel 分渠道） */
export async function getAlertPrecision(q?: {
  dateFrom?: string;
  dateTo?: string;
  alert_type?: string;
  /** call=通话 / chat=在线会话；空=全部渠道 */
  channel?: 'call' | 'chat';
}): Promise<AlertPrecisionStats> {
  const data: any = await get('/quality/stats/alert-precision', q);
  return (data || { summary: {}, by_type: [], by_rule: [], by_day: [] }) as AlertPrecisionStats;
}

export interface EmotionSeriesRow {
  id: number;
  uuid?: string;
  cdr_id?: number;
  agent_id?: number;
  emotion?: string; // 正面|中性|焦虑|愤怒
  level?: string; // 低|中|高
  complaint_type?: string;
  escalation_prob?: number;
  utterance?: string;
  created_at?: string;
}

/** GET /quality/emotion-series?cdr_id=&uuid=（admin/supervisor；至少其一） */
export async function listEmotionSeries(q: { cdr_id?: number; uuid?: string }): Promise<EmotionSeriesRow[]> {
  const data: any = await get('/quality/emotion-series', q);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.list)) return data.list;
  return [];
}

/** 违规项人工复核（confirm|misjudge|rescore）；note 为复核分析（必填）；rescore 的 score 为正值扣分（0–100，20 的倍数） */
export function reviewViolation(reportId: number, vid: number, v: { action: 'confirm' | 'misjudge' | 'rescore'; score?: number; note: string }) {
  return post(`/quality/reports/${reportId}/violations/${vid}/review`, v);
}

/** POST /quality/reports/:id/violations —— 人工补检（admin/supervisor，AI 漏检覆盖） */
export function addViolation(reportId: number, v: AddViolationIn) {
  return post(`/quality/reports/${reportId}/violations`, v);
}

/** GET /quality/reports/:id（详情含 transcript） */
export function getReport(id: number): Promise<QualityReport> {
  return get(`/quality/reports/${id}`, undefined, { silent: true });
}

